'use client';

// Jigyasa public homepage — structure modelled on Kreta's proven ladder:
// who it's for → industries → features → how fast → price → closing push.
// Aumonline branding features prominently (header + footer).

import React, { useState } from "react";
import {
  Home, Sofa, Stethoscope, Building2, Zap, PhoneCall, Columns3,
  MessageCircle, BarChart3, SearchX, Check, Users, Contact, Upload, Flame,
} from "lucide-react";
import { usePricing } from "./Billing";

const FONT = { fontFamily: "'Hind', 'IBM Plex Sans', system-ui, sans-serif" };

// Real social proof only (owner rule: no invented testimonials). Leave empty
// until real quotes exist — the section renders nothing while this is empty.
const TESTIMONIALS = [];

export const Homepage = ({ onLogin, onStart }) => (
  <div className="min-h-screen bg-white text-zinc-900" style={FONT}>
    <Header onLogin={onLogin} />
    <Hero onStart={onStart} />
    <Industries />
    <Features />
    <QuickStart />
    <Testimonials />
    <PricingPromise />
    <Pricing onStart={onStart} />
    <Closing onStart={onStart} />
    <Footer />
  </div>
);

const Header = ({ onLogin }) => (
  <header data-testid="homepage-header" className="sticky top-0 z-30 border-b border-zinc-200 bg-white/95 backdrop-blur">
    <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
      <img src="/aumonline-logo.png" alt="Aumonline" className="h-9 w-auto" data-testid="aumonline-logo-header" />
      <span className="h-6 w-px bg-zinc-200" />
      <span className="text-lg font-semibold tracking-tight">JIGYASA</span>
      <button
        data-testid="homepage-login-btn"
        onClick={onLogin}
        className="ml-auto rounded-full bg-zinc-900 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-700"
      >
        Login
      </button>
    </div>
  </header>
);

const Hero = ({ onStart }) => (
  <section className="bg-zinc-950 text-zinc-100">
    <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.2fr_1fr]">
      <div>
        <div className="mb-4 text-sm font-medium uppercase tracking-[0.25em] text-rose-400">
          जिज्ञासा · For businesses that live on enquiries
        </div>
        <h1 className="mb-5 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          Never lose another enquiry.
        </h1>
        <p className="mb-8 max-w-xl text-base text-zinc-300 sm:text-lg">
          Every walk-in, call and website lead — captured in 10 seconds, followed
          up on time, and never forgotten. On your phone or your desk.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="hero-start-btn"
            onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full bg-rose-600 px-7 py-3 font-medium text-white transition-colors hover:bg-rose-500"
          >
            Start free
          </button>
          <button
            data-testid="hero-how-btn"
            onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full border border-zinc-600 px-7 py-3 font-medium text-zinc-200 transition-colors hover:bg-zinc-800"
          >
            See how it works
          </button>
        </div>
        <p className="mt-4 text-sm text-zinc-400">14-day free trial · No credit card needed</p>
      </div>

      {/* Mini product glimpse */}
      <div className="hidden lg:block">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-zinc-200">Call back today</span>
            <span className="rounded-full bg-red-700 px-2 py-0.5 text-xs font-semibold text-white">3</span>
          </div>
          {[
            ["Ramesh Kumar", "Site visit scheduled", "2d late"],
            ["Dr. Priya's referral", "Sent estimate", "due today"],
            ["Sunrise Interiors lead", "Spoke to them", "due today"],
          ].map(([name, stage, due]) => (
            <div key={name} className="mb-2 rounded-xl bg-zinc-800 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-100">{name}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-400">{due}</span>
              </div>
              <div className="mt-0.5 text-xs text-zinc-400">{stage}</div>
            </div>
          ))}
          <div className="mt-3 rounded-xl border border-dashed border-zinc-700 p-3 text-center text-xs text-zinc-500">
            + Add new enquiry — name, phone, source. Done.
          </div>
        </div>
      </div>
    </div>
  </section>
);

const INDUSTRIES = [
  { icon: Home, name: "Real estate", line: "Track every buyer from first call to booking.", chips: ["Site visit scheduled", "Sent quote", "Booked"] },
  { icon: Sofa, name: "Interiors & architecture", line: "From first brief to signed project.", chips: ["Site visited", "Sent proposal", "Project signed"] },
  { icon: Stethoscope, name: "Clinics", line: "Every patient enquiry, followed up with care.", chips: ["Appointment booked", "Sent estimate", "Treatment started"] },
  { icon: Building2, name: "Coworking spaces", line: "Walk-ins to signed members, nothing slips.", chips: ["Visit scheduled", "Sent offer letter", "Signed up"] },
];

const Industries = () => (
  <section className="bg-white">
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h2 className="text-base font-semibold uppercase tracking-wider text-rose-700 md:text-lg">Made for your business</h2>
      <p className="mb-8 mt-2 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl">
        Jigyasa speaks your industry's language — pick yours when you sign up.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {INDUSTRIES.map(({ icon: Icon, name, line, chips }) => (
          <div
            key={name}
            data-testid={`industry-card-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 transition-shadow hover:shadow-md"
          >
            <Icon size={22} className="text-rose-700" />
            <h3 className="mt-3 text-lg font-semibold">{name}</h3>
            <p className="mt-1 text-sm text-zinc-600">{line}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <span key={c} className="rounded-full border border-zinc-300 bg-white px-2.5 py-0.5 text-xs text-zinc-700">{c}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p data-testid="industries-others-note" className="mt-6 text-center text-sm text-zinc-500">
        Run a different kind of business? <span className="font-medium text-zinc-800">Jigyasa works for everyone</span> — just pick “Something else” when you sign up.
      </p>
    </div>
  </section>
);

const FEATURES = [
  { icon: Zap, title: "10-second capture", body: "Name, phone, where they heard of you. That's all it takes — details can come later." },
  { icon: PhoneCall, title: "Call back today", body: "Every morning, a clear list of who's waiting on you. Overdue ones flagged in red." },
  { icon: Columns3, title: "Pipeline at a glance", body: "Every enquiry on one board, stage by stage — nothing hides in a notebook." },
  { icon: MessageCircle, title: "WhatsApp follow-ups", body: "Ready-made messages with the customer's name filled in — opened in WhatsApp with one tap." },
  { icon: BarChart3, title: "Owner's business view", body: "This month vs last month, where leads come from, and your recent wins — on your phone." },
  { icon: SearchX, title: "Why-we-lose insights", body: "Every lost lead gets a reason. See the patterns before they cost you the next one." },
  { icon: Users, title: "Your whole team", body: "Give your staff their own logins with the right access. Everyone works off the same enquiries — nothing in private notebooks." },
  { icon: Contact, title: "Customer lists, built for you", body: "Win a deal and the customer is saved automatically — companies and individuals, each in their own list." },
  { icon: Upload, title: "Bring your data in minutes", body: "Already have customers in Excel? One-click import gets your whole history in on day one." },
];

const Features = () => (
  <section id="features" className="bg-zinc-50">
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h2 className="text-base font-semibold uppercase tracking-wider text-rose-700 md:text-lg">What you get</h2>
      <p className="mb-8 mt-2 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl">
        Everything you need to stop losing enquiries. Nothing you don't.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-2xl border border-zinc-200 bg-white p-6"
          >
            <Icon size={20} className="text-rose-700" />
            <h3 className="mt-3 font-semibold">{title}</h3>
            <p className="mt-1 text-sm text-zinc-600">{body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const QuickStart = () => (
  <section className="bg-zinc-900 text-zinc-100">
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Live in 10 minutes.</h2>
      <p className="mt-2 text-zinc-400">No training. No setup calls. No spreadsheets to migrate.</p>
      <div className="mt-8 grid gap-6 sm:grid-cols-3">
        {[
          ["1", "Sign up with your mobile", "A code by SMS, a PIN of your choice — that's your account."],
          ["2", "Pick your industry", "Jigyasa instantly talks the way your business talks."],
          ["3", "Add your first enquiry", "That's it. You're live, and nothing slips from today."],
        ].map(([n, t, b]) => (
          <div key={n} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6">
            <div className="text-3xl font-semibold text-rose-400">{n}</div>
            <h3 className="mt-2 font-semibold">{t}</h3>
            <p className="mt-1 text-sm text-zinc-400">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PLAN_CARDS = [
  {
    key: "solo", name: "Solo", tagline: "For a one-person business",
    monthly: 495, yearly: 4950,
    features: [
      "Unlimited enquiries",
      "Pipeline, call-back-today & WhatsApp follow-ups",
      "Owner's business view & why-we-lose insights",
      "Your industry's language, built in",
      "Works on phone + desktop",
    ],
  },
  {
    key: "team", name: "Team", tagline: "For a business with a team", popular: true,
    monthly: 975, yearly: 9750,
    features: [
      "Everything in Solo, plus —",
      "Your whole team — logins, roles & invites",
      "Team pulse — who holds what, what's overdue, who's winning",
      "Printable meeting sheet for every staff member",
      "Assign every enquiry — \"who's handling this?\"",
      "Customer lists & one-click Excel import",
    ],
  },
];

const Testimonials = () => {
  if (TESTIMONIALS.length === 0) return null;
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="text-base font-semibold uppercase tracking-wider text-rose-700 md:text-lg">What owners say</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
              <p className="text-sm text-zinc-700">"{t.quote}"</p>
              <div className="mt-3 text-sm font-semibold">{t.name}</div>
              <div className="text-xs text-zinc-500">{t.business}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingPromise = () => (
  <section data-testid="pricing-promise" className="bg-rose-50">
    <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6">
      <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Our pricing promise</h2>
      <p className="mx-auto mt-4 max-w-xl text-base text-zinc-700">
        Ever ordered a ₹200 dish and paid ₹260 after "taxes and charges"?
        <span className="font-semibold"> We hate that too.</span>
      </p>
      <div className="mx-auto mt-6 grid max-w-2xl gap-3 text-left sm:grid-cols-2">
        {[
          ["The price is the price", "The number on this page is the final rupee out of your pocket. Nothing added at checkout. Ever."],
          ["No per-user charges", "One flat price. Your whole team is included in Team — hire freely, pay the same."],
          ["No add-ons, no surprises", "Every feature listed is included. There is nothing extra to buy."],
          ["Cancel anytime", "No lock-in, no cancellation fee, no phone call needed. Your data stays yours."],
        ].map(([t, b]) => (
          <div key={t} className="rounded-2xl border border-rose-200 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <Check size={15} className="shrink-0 text-rose-600" /> {t}
            </div>
            <p className="mt-1 text-xs text-zinc-600">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Pricing = ({ onStart }) => {
  const [cycle, setCycle] = useState("yearly");
  const yearly = cycle === "yearly";
  const pricing = usePricing();
  return (
    <section id="pricing" className="bg-white">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="text-center">
          <h2 className="text-base font-semibold uppercase tracking-wider text-rose-700 md:text-lg">Pricing</h2>
          <p className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">Two simple plans. Free for 14 days.</p>
          <p className="mt-2 text-sm text-zinc-500">Try every feature free — pick a plan only when you're ready.</p>
          <div className="mt-6 inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 p-1" data-testid="billing-toggle">
            <button
              data-testid="billing-toggle-monthly"
              onClick={() => setCycle("monthly")}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${!yearly ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-toggle-yearly"
              onClick={() => setCycle("yearly")}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${yearly ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
            >
              Yearly <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${yearly ? "bg-rose-600 text-white" : "bg-rose-100 text-rose-700"}`}>2 months free</span>
            </button>
          </div>
        </div>
        <div className="mx-auto mt-8 grid max-w-3xl gap-5 sm:grid-cols-2">
          {PLAN_CARDS.map((raw) => {
            const isTeam = raw.key === "team";
            const p = isTeam && pricing?.team ? { ...raw, monthly: pricing.team.monthly, yearly: pricing.team.yearly } : raw;
            const foundingActive = isTeam && pricing?.founding?.active;
            const strike = foundingActive && pricing?.team_standard
              ? (yearly ? Math.floor(pricing.team_standard.yearly / 12) : pricing.team_standard.monthly)
              : null;
            return (
            <div
              key={p.key}
              data-testid={`pricing-card-${p.key}`}
              className={`relative rounded-3xl bg-white p-7 ${p.popular ? "border-2 border-rose-600 shadow-xl" : "border border-zinc-200 shadow-sm"}`}
            >
              {foundingActive ? (
                <span className="absolute -top-3 right-6 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white">Founding price</span>
              ) : p.popular && (
                <span className="absolute -top-3 right-6 rounded-full bg-rose-600 px-3 py-1 text-xs font-semibold text-white">Most popular</span>
              )}
              <h3 className="text-lg font-semibold tracking-tight text-zinc-900">{p.name}</h3>
              <p className="text-sm text-zinc-500">{p.tagline}</p>
              {foundingActive && (
                <div data-testid="founding-ticker" className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  <span className="flex items-center gap-1.5"><Flame size={13} className="shrink-0 text-amber-600" /> First 10 businesses only</span>
                  <span data-testid="founding-claimed-count" className="shrink-0 rounded-full bg-amber-500 px-2 py-0.5 text-white">{pricing.founding.claimed} of {pricing.founding.total} claimed</span>
                </div>
              )}
              {yearly ? (
                <>
                  <div className="mt-4 flex items-baseline gap-2">
                    {strike && <span className="text-xl text-zinc-400 line-through">₹{strike}</span>}
                    <span className="text-4xl font-semibold tracking-tight">₹{Math.floor(p.yearly / 12)}</span>
                    <span className="text-zinc-500">/ month</span>
                  </div>
                  <p className="mt-1 text-xs text-zinc-500">₹{p.yearly.toLocaleString("en-IN")} billed yearly · 2 months free</p>
                </>
              ) : (
                <>
                  <div className="mt-4 flex items-baseline gap-2">
                    {strike && <span className="text-xl text-zinc-400 line-through">₹{strike}</span>}
                    <span className="text-4xl font-semibold tracking-tight">₹{p.monthly}</span>
                    <span className="text-zinc-500">/ month</span>
                  </div>
                  <p className="mt-1 text-xs text-zinc-500">Billed monthly · cancel anytime</p>
                </>
              )}
              {foundingActive && (
                <p className="mt-1 text-xs font-medium text-amber-700">
                  Yours for life at this price — goes to ₹{yearly ? Math.floor(pricing.team_standard.yearly / 12).toLocaleString("en-IN") : pricing.team_standard.monthly.toLocaleString("en-IN")}/mo when the 10 slots are gone.
                </p>
              )}
              <ul className="mt-5 space-y-2.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 shrink-0 text-rose-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-${p.key}-start-btn`}
                onClick={() => onStart(`${p.key}-${cycle}`)}
                className={`mt-7 w-full rounded-full py-3.5 font-medium transition-colors ${
                  p.popular ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-zinc-900 text-white hover:bg-zinc-700"
                }`}
              >
                Start 14-day Free Trial
              </button>
              <p className="mt-2 text-center text-xs text-zinc-400">No credit card required · Cancel anytime</p>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Closing = ({ onStart }) => (
  <section className="bg-zinc-950 text-zinc-100">
    <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
      <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
        Your next customer just enquired somewhere.
      </h2>
      <p className="mt-2 text-lg text-zinc-400">Will you remember to call them back?</p>
      <button
        data-testid="closing-start-btn"
        onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
        className="mt-7 rounded-full bg-rose-600 px-8 py-3.5 font-medium text-white transition-colors hover:bg-rose-500"
      >
        Start free
      </button>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="homepage-footer" className="border-t border-zinc-200 bg-white">
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
        <div>
          <img src="/aumonline-logo.png" alt="Aumonline" className="h-12 w-auto" data-testid="aumonline-logo-footer" />
          <p className="mt-2 text-sm text-zinc-600">
            Jigyasa is <a href="https://aumonline.in" target="_blank" rel="noreferrer" className="font-medium text-zinc-800 underline-offset-4 hover:underline" data-testid="footer-aumonline-link">a product of Aumonline</a>.
          </p>
        </div>
        <div className="text-sm text-zinc-500">
          <div className="mb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">The Aumonline family</div>
          <div data-testid="aumonline-family">Kreta · Apoopika · Ashraya · Jigyasa</div>
        </div>
      </div>
      <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 border-t border-zinc-100 pt-4 text-xs text-zinc-500" data-testid="footer-links">
        <a href="https://aumonline.in/terms" target="_blank" rel="noreferrer" className="hover:text-zinc-800" data-testid="footer-terms">Terms</a>
        <a href="https://aumonline.in/privacy" target="_blank" rel="noreferrer" className="hover:text-zinc-800" data-testid="footer-privacy">Privacy</a>
        <a href="https://aumonline.in/refund" target="_blank" rel="noreferrer" className="hover:text-zinc-800" data-testid="footer-refund">Refund &amp; Cancellation</a>
        <a href="mailto:ashwath@aumonline.in" className="hover:text-zinc-800" data-testid="footer-email">ashwath@aumonline.in</a>
        <a href="https://aumonline.in/contact" target="_blank" rel="noreferrer" className="hover:text-zinc-800" data-testid="footer-contact">Contact</a>
        <span className="ml-auto text-zinc-400">© 2026 Aumonline. All rights reserved.</span>
      </div>
    </div>
  </footer>
);
